import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Trash2, Minus, Plus, ShoppingBag, ArrowRight, Palette } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import DesignPreview from "@/components/DesignPreview";

function ItemThumb({ item }) {
  if (item.product_id === "custom" && item.custom) {
    return (
      <div className="w-20 h-20 bg-brand-sky/40 border-2 border-ink rounded-xl grid place-items-center p-1 shrink-0">
        <DesignPreview custom={item.custom} className="w-14 h-14" compact />
      </div>
    );
  }
  return <img src={item.image} alt={item.name} className="w-20 h-20 object-cover border-2 border-ink rounded-xl shrink-0" />;
}

export default function Cart() {
  const { items, removeItem, updateQty, subtotal, clear } = useCart();
  const [loading, setLoading] = useState(false);
  const shipping = subtotal >= 50 || subtotal === 0 ? 0 : 6.99;
  const total = subtotal + shipping;

  const checkout = async () => {
    setLoading(true);
    try {
      const payload = {
        items: items.map((i) => ({
          product_id: i.product_id,
          size: i.size,
          color: i.color,
          quantity: i.quantity,
          custom: i.custom || null,
        })),
        origin_url: window.location.origin,
      };
      const { data } = await api.post("/checkout/session", payload);
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Checkout failed");
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="cart-empty">
        <div className="w-20 h-20 mx-auto bg-brand-yellow neo rounded-2xl grid place-items-center mb-6"><ShoppingBag className="w-9 h-9" /></div>
        <h1 className="font-display font-bold text-4xl">Your cart is empty</h1>
        <p className="mt-3 font-semibold text-ink/70">Time to fabricate something fresh.</p>
        <div className="mt-8 flex flex-wrap gap-4 justify-center">
          <Link to="/shop" className="bg-ink text-paper font-bold px-6 py-3.5 rounded-xl neo-press border-2 border-ink inline-flex items-center gap-2">Shop tees <ArrowRight className="w-5 h-5" /></Link>
          <Link to="/designer" className="bg-brand-sky font-bold px-6 py-3.5 rounded-xl neo-press border-2 border-ink inline-flex items-center gap-2"><Palette className="w-5 h-5" /> Design one</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12" data-testid="cart-page">
      <h1 className="font-display font-bold text-4xl sm:text-5xl mb-8">Your Cart</h1>
      <div className="grid gap-8 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-4">
          {items.map((item) => (
            <div key={item.key} className="bg-white neo rounded-2xl p-4 flex gap-4 items-center" data-testid={`cart-item-${item.key}`}>
              <ItemThumb item={item} />
              <div className="flex-1 min-w-0">
                <h3 className="font-display font-bold text-lg leading-tight">{item.name}</h3>
                <p className="text-sm font-bold text-ink/60">{item.color} · {item.size}</p>
                {item.custom?.text && <p className="text-xs font-semibold text-ink/50">“{item.custom.text}”</p>}
                <p className="font-display font-bold mt-1">${item.price.toFixed(2)}</p>
              </div>
              <div className="flex flex-col items-end gap-2">
                <button onClick={() => removeItem(item.key)} className="text-ink/50 hover:text-brand-pink" data-testid={`remove-${item.key}`}><Trash2 className="w-5 h-5" /></button>
                <div className="flex items-center bg-white border-2 border-ink rounded-lg overflow-hidden">
                  <button onClick={() => updateQty(item.key, item.quantity - 1)} className="p-2 hover:bg-brand-yellow"><Minus className="w-3.5 h-3.5" /></button>
                  <span className="w-8 text-center font-bold text-sm">{item.quantity}</span>
                  <button onClick={() => updateQty(item.key, item.quantity + 1)} className="p-2 hover:bg-brand-yellow"><Plus className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            </div>
          ))}
          <button onClick={clear} className="text-sm font-bold text-ink/50 hover:text-brand-pink" data-testid="clear-cart">Clear cart</button>
        </div>

        <div className="bg-brand-yellow neo-lg rounded-3xl p-6 h-fit sticky top-24" data-testid="order-summary">
          <h2 className="font-display font-bold text-2xl mb-5">Summary</h2>
          <div className="space-y-3 font-bold">
            <div className="flex justify-between"><span>Subtotal</span><span data-testid="summary-subtotal">${subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between"><span>Shipping</span><span>{shipping === 0 ? "FREE" : `$${shipping.toFixed(2)}`}</span></div>
            {shipping > 0 && <p className="text-xs font-semibold text-ink/60">Add ${(50 - subtotal).toFixed(2)} more for free shipping.</p>}
            <div className="border-t-2 border-ink pt-3 flex justify-between font-display text-xl"><span>Total</span><span data-testid="summary-total">${total.toFixed(2)}</span></div>
          </div>
          <button onClick={checkout} disabled={loading} className="w-full mt-6 bg-ink text-paper font-bold px-6 py-4 rounded-xl neo-press border-2 border-ink inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="checkout-btn">
            {loading ? "Redirecting…" : <>Checkout <ArrowRight className="w-5 h-5" /></>}
          </button>
          <p className="text-xs font-semibold text-center text-ink/60 mt-3">Secured by Stripe</p>
        </div>
      </div>
    </div>
  );
}
